import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, BarChart3, History, Scale } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Home = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [leftText, setLeftText] = useState('');
  const [rightText, setRightText] = useState('');
  const [compareResult, setCompareResult] = useState<{
    added: string[];
    removed: string[];
    sharedCount: number;
    leftUnique: Set<string>;
    rightUnique: Set<string>;
  } | null>(null);

  const MAX_WORDS = 1000;

  const getWords = (text: string) =>
    (text.match(/[A-Za-zÀ-ÿ0-9'-]+/g) || []).map(w => w.toLowerCase());

  const buildHighlightedHtml = (text: string, uniqueWords: Set<string>) => {
    const tokens = text.match(/[A-Za-zÀ-ÿ0-9'-]+|[^A-Za-zÀ-ÿ0-9'-]+/g) || [];
    const html = tokens
      .map(token => {
        const lower = token.toLowerCase();
        if (uniqueWords.has(lower)) {
          return `<span class="bg-red-100 text-red-700 px-1 rounded">${token}</span>`;
        }
        return token;
      })
      .join('');
    return html;
  };

  const handleCompare = () => {
    const leftWords = getWords(leftText);
    const rightWords = getWords(rightText);

    if (leftWords.length === 0 || rightWords.length === 0) {
      toast.error('Saisir du texte dans les deux zones avant de comparer');
      return;
    }

    if (leftWords.length > MAX_WORDS || rightWords.length > MAX_WORDS) {
      toast.error(`La comparaison est limitée à ${MAX_WORDS} mots par zone`);
      return;
    }

    const leftSet = new Set(leftWords);
    const rightSet = new Set(rightWords);

    const added = Array.from(rightSet).filter(word => !leftSet.has(word));
    const removed = Array.from(leftSet).filter(word => !rightSet.has(word));
    const sharedCount = Array.from(leftSet).filter(word => rightSet.has(word)).length;

    setCompareResult({ 
      added, 
      removed, 
      sharedCount,
      leftUnique: new Set(removed),
      rightUnique: new Set(added)
    });
    toast.success('Comparaison effectuée');
  };

  const leftCount = getWords(leftText).length;
  const rightCount = getWords(rightText).length;

  const features = [
    {
      icon: <Upload className="h-12 w-12 text-blue-600" />,
      title: "Importer des fichiers",
      description: "Ajoutez vos données (.json, .csv, .xlsx, .xls, .txt) et laissez l'application nettoyer le contenu automatiquement.",
      path: "/upload"
    },
    {
      icon: <BarChart3 className="h-12 w-12 text-blue-600" />,
      title: "Comparer des fichiers",
      description: "Choisissez deux imports du même format et visualisez instantanément les différences.",
      path: "/compare"
    },
    {
      icon: <History className="h-12 w-12 text-blue-600" />,
      title: "Historique",
      description: "Retrouvez vos imports, les dates et relancez une comparaison en un clic.",
      path: "/history"
    }
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Comparez vos contenus en un clin d'œil
        </h1>
        <p className="text-xl text-gray-600">
          Collez vos textes et visualisez instantanément ce qui change (limite {MAX_WORDS} mots par zone).
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-12">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Texte gauche</label>
              <span className={`text-xs ${leftCount > MAX_WORDS ? 'text-red-600' : 'text-gray-500'}`}>
                {leftCount}/{MAX_WORDS} mots
              </span>
            </div>
            <textarea
              value={leftText}
              onChange={(e) => setLeftText(e.target.value)}
              className="w-full h-48 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Collez ou saisissez votre texte..."
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Texte droit</label>
              <span className={`text-xs ${rightCount > MAX_WORDS ? 'text-red-600' : 'text-gray-500'}`}>
                {rightCount}/{MAX_WORDS} mots
              </span>
            </div>
            <textarea
              value={rightText}
              onChange={(e) => setRightText(e.target.value)}
              className="w-full h-48 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Collez ou saisissez votre texte..."
            />
          </div>
        </div>

        {compareResult && (
          <div className="mt-4 grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 border rounded-md p-3">
              <div className="text-xs text-gray-500 mb-2">Différences sur le texte gauche</div>
              <div
                className="text-sm text-gray-800 leading-relaxed"
                dangerouslySetInnerHTML={{ 
                  __html: buildHighlightedHtml(leftText, compareResult.leftUnique) 
                }}
              />
            </div>
            <div className="bg-gray-50 border rounded-md p-3">
              <div className="text-xs text-gray-500 mb-2">Différences sur le texte droit</div>
              <div
                className="text-sm text-gray-800 leading-relaxed"
                dangerouslySetInnerHTML={{ 
                  __html: buildHighlightedHtml(rightText, compareResult.rightUnique) 
                }}
              />
            </div>
          </div>
        )}

        <div className="flex justify-center mt-6">
          <button
            onClick={handleCompare}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <Scale className="h-5 w-5" />
            Comparer
          </button>
        </div>

        {compareResult && (
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
              <h3 className="text-blue-800 font-semibold mb-2">Mots communs</h3>
              <p className="text-sm text-blue-700">{compareResult.sharedCount} mots présents dans les deux textes.</p>
            </div>
            <div className="bg-green-50 border border-green-100 rounded-lg p-4">
              <h3 className="text-green-800 font-semibold mb-2">Ajouts (droite)</h3>
              <p className="text-sm text-green-700 mb-2">{compareResult.added.length} mots uniquement dans le texte de droite.</p>
              <div className="text-xs text-green-800 break-words">
                {compareResult.added.slice(0, 50).join(', ') || 'Aucun'}
              </div>
            </div>
            <div className="bg-red-50 border border-red-100 rounded-lg p-4">
              <h3 className="text-red-800 font-semibold mb-2">Suppressions (gauche)</h3>
              <p className="text-sm text-red-700 mb-2">{compareResult.removed.length} mots uniquement dans le texte de gauche.</p>
              <div className="text-xs text-red-800 break-words">
                {compareResult.removed.slice(0, 50).join(', ') || 'Aucun'}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-8 mb-12">
        {features.map((feature, index) => (
          <div
            key={index}
            className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => currentUser ? navigate(feature.path) : navigate('/login')}
          >
            <div className="flex justify-center mb-4">
              {feature.icon}
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2 text-center">
              {feature.title}
            </h3>
            <p className="text-gray-600 text-center">
              {feature.description}
            </p>
          </div>
        ))}
      </div>


    </div>
  );
};

export default Home;
